import {
  computed,
  DestroyRef,
  DOCUMENT,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, fromEvent, map, of, switchMap, take, timer } from 'rxjs';

import {
  LOADING_SIZING,
  LOADING_TIMINGS,
  oneWholeHopMs,
  resolveSize,
  showsLoadingScreenFor,
  staticPhaseDurationMs,
  wordmarkWidthPx,
} from './app-loading.config';
import { AppPreloadService } from './app-preload.service';

/**
 * Where the loading screen is in its choreography (SB-3521).
 *
 * - `dynamic`  load-driven: the O bounces for as long as the preload runs
 * - `static`   fixed choreography: the O settles and the letters unfold
 * - `exiting`  fading into the destination page
 * - `done`     gone; the platform is in front
 */
export type LoadingPhase = 'dynamic' | 'static' | 'exiting' | 'done';

/**
 * Drives the blocking loading screen's phases.
 *
 * On the routes it covers the screen plays every time - there is no threshold
 * and no fast path that skips it - design confirmed this over the spec's US-2
 * render-delay guard. That is the team's call: the added time buys a
 * sequence that is always seen whole, rather than one that appears only on slow
 * connections and so is never designed against.
 *
 * Which routes it covers is {@link showsLoadingScreenFor}. Anywhere else - the
 * login page above all - the service starts `done`, so the page paints
 * immediately and this class does nothing at all.
 */
@Injectable({ providedIn: 'root' })
export class AppLoadingService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly preload = inject(AppPreloadService);

  /** Whether this load is one the loading screen covers at all. */
  private readonly coversThisLoad = showsLoadingScreenFor(
    this.document.location.pathname,
  );

  /**
   * On a covered route the screen is up from the moment this service is
   * constructed. `AppComponent` injects it, so that happens while the root
   * component is created - before any routed content can paint.
   *
   * Deliberately not a `timer(0)` that flips to `dynamic`: a zero-delay timer
   * resolves on a macrotask, while the router's first navigation can finish
   * inside microtasks when its lazy chunk is already cached. Painting from a
   * timer made the loader appear on some reloads and not others.
   *
   * Everywhere else this starts `done`, which is the phase meaning "gone, the
   * platform is in front" - so the destination renders on the first frame.
   */
  private readonly _phase = signal<LoadingPhase>(
    this.coversThisLoad ? 'dynamic' : 'done',
  );
  private readonly paintedAt = Date.now();

  public readonly phase = this._phase.asReadonly();

  /** Whether the loading screen itself is on screen. */
  public readonly isLoaderVisible = computed(() => {
    const phase = this._phase();
    return phase === 'dynamic' || phase === 'static' || phase === 'exiting';
  });

  /**
   * Whether the platform may render behind the loader. True from `exiting` so
   * the destination is already painted underneath while the loader fades out -
   * the spec calls for a fade into the page, never an instant cut.
   */
  public readonly isContentVisible = computed(
    () => this._phase() === 'exiting' || this._phase() === 'done',
  );

  /**
   * True when the viewer has asked the OS to reduce motion. The bounce and the
   * letter unfold are suppressed for them; the logo, the hold and the exit fade
   * still play. Read once - a mid-load change of this setting is not worth
   * restarting the choreography for.
   */
  public readonly prefersReducedMotion =
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  constructor() {
    if (!this.coversThisLoad) {
      return;
    }

    toObservable(this.preload.isFirstNavigationComplete)
      .pipe(
        filter(Boolean),
        take(1),
        // Hold the bounce for its floor, measured from first paint.
        switchMap(() => timer(this.remainingDynamicMs())),
        // If the tab is backgrounded, hold the reveal until it comes back so it
        // plays from its start rather than resuming from a stale frame.
        switchMap(() => this.whenDocumentVisible()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.beginReveal());
  }

  private remainingDynamicMs(): number {
    return Math.max(0, this.minimumDynamicMs() - (Date.now() - this.paintedAt));
  }

  /**
   * How long the screen must stay up before the reveal may start.
   *
   * The spec's anti-blink minimum, measured from first paint - US-3 spells the
   * arithmetic out: rendered at T=300, preload complete at T=450, reveal begins
   * at T=800. It is a floor against blinking, not a budget the screen is padded
   * out to. The dynamic phase's length is the preload's own, which is what
   * "its duration is determined by real preload time, not choreography" and
   * "elapsed time alone never changes the screen" require.
   *
   * The anti-blink minimum alone is not enough, and this is a **knowing
   * deviation from the spec** rather than an oversight - record it as such.
   *
   * One complete hop costs {@link oneWholeHopMs} (1125ms at the shipped
   * numbers), because the bounce's motion runs to 55% of its cycle. The spec's
   * 500ms floor cannot contain that at any `bounceStartMs`: even starting the
   * hop at 0 would need a 909ms cycle, and design's note on the keyframes rules
   * shortening it out ("shortening it turns the hop into a jitter"). So a
   * restful 1500ms bounce and a 500ms floor are simply incompatible, and
   * holding for the shorter one shows a hop cut off mid-rise.
   *
   * Holding to one whole hop is the smallest floor that still shows the gesture
   * design drew. It is derived from the choreography, not picked - change the
   * cycle and it follows. It is 1125ms against the 2300ms this replaced, so the
   * screen costs about 1.2s less per load while still contradicting §1's
   * "elapsed time alone never changes the screen."
   *
   * The spec's own answer was US-2: don't show the loader on a load this fast,
   * so the question of padding never arises. Design has since confirmed the
   * always-show behaviour instead, which removes that escape and makes this
   * floor load-bearing - without it the hop is truncated on most loads, not
   * just the rare quick one.
   *
   * None of this applies under reduced motion, where there is no hop to wait
   * for - the mark's animation is off entirely - so holding past the anti-blink
   * minimum would buy nothing but a longer stare at a static logo.
   */
  private minimumDynamicMs(): number {
    return this.prefersReducedMotion
      ? LOADING_TIMINGS.minimumDisplayMs
      : Math.max(LOADING_TIMINGS.minimumDisplayMs, oneWholeHopMs());
  }

  private whenDocumentVisible() {
    if (this.document.visibilityState === 'visible') {
      return of(true);
    }
    return fromEvent(this.document, 'visibilitychange').pipe(
      filter(() => this.document.visibilityState === 'visible'),
      take(1),
      map(() => true),
    );
  }

  private beginReveal(): void {
    this._phase.set('static');

    timer(this.staticDurationMs())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._phase.set('exiting');
        timer(LOADING_TIMINGS.exitFadeMs)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this._phase.set('done'));
      });
  }

  /**
   * The reveal's length, derived from the last letter's landing plus the hold -
   * never an absolute timestamp, so it stays correct as the stagger scales with
   * viewport width. The stage is `fixed inset-0`, so its width is the viewport's.
   */
  private staticDurationMs(): number {
    const wordmarkHeight = resolveSize(
      window.innerWidth,
      LOADING_SIZING.wordmark,
    );
    return staticPhaseDurationMs(
      wordmarkWidthPx(wordmarkHeight),
      this.prefersReducedMotion,
    );
  }
}
